/**
 * TemplateRuntimeService.js
 *
 * Runtime services for publishing Templates and applying them as workspace Modules.
 */

// Firebase
import { Timestamp, collection, deleteDoc, doc, getDocs, onSnapshot, query, setDoc, where } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { auth, collections, db, functions, httpsCallable, storage } from 'firebaseConfig';

// Security
import { isCurrentUserSuperUser } from 'security/superUser';

// Utilities
import { generateKey } from 'utilities/Keys';

const normalize = (value) => String(value || '').trim();
const normalizeLower = (value) => normalize(value).toLowerCase();
const safeArray = (value) => (Array.isArray(value) ? value : []);

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? 0 : parsed;
}

function cleanDoc(row) {
  if (!row || typeof row !== 'object') return {};
  const next = { ...row };
  delete next.id;
  return next;
}

function collectAliases(...values) {
  return Array.from(new Set(values.map((value) => normalize(value)).filter(Boolean)));
}

async function fetchWorkspaceNodeTypes(workspaceKey) {
  const snap = await getDocs(collection(db, collections.workspaces, workspaceKey, collections.nodetypes));
  return snap.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) }));
}

async function fetchWorkspaceDatabases(workspaceKey) {
  const q = query(collection(db, collections.collections), where('workspaceKey', '==', workspaceKey));
  const snap = await getDocs(q);
  return snap.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) }));
}

async function fetchWorkspaceViews(workspaceKey) {
  const q = query(collection(db, collections.views), where('workspaceKey', '==', workspaceKey));
  const snap = await getDocs(q);
  return snap.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) }));
}

async function fetchWorkspaceDashboards(workspaceKey) {
  const q = query(collection(db, collections.dashboards), where('workspaceKey', '==', workspaceKey));
  const snap = await getDocs(q);
  return snap.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) }));
}

async function fetchWorkspaceNodes(workspaceKey) {
  const q = query(collection(db, collections.nodes), where('workspaceKey', '==', workspaceKey));
  const snap = await getDocs(q);
  return snap.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) }));
}

async function fetchWorkspaceIncidentEdges(workspaceNodes = []) {
  const nodeKeys = Array.from(new Set(
    safeArray(workspaceNodes)
      .map((row) => normalize(row?.key))
      .filter(Boolean)
  ));

  if (!nodeKeys.length) return [];

  const edgesRef = collection(db, collections.edges);
  const edgeMap = new Map();
  const BATCH_SIZE = 20;

  for (let i = 0; i < nodeKeys.length; i += BATCH_SIZE) {
    const batch = nodeKeys.slice(i, i + BATCH_SIZE);
    const snaps = await Promise.all(
      batch.flatMap((nodeKey) => ([
        getDocs(query(edgesRef, where('sourceNodeKey', '==', nodeKey))),
        getDocs(query(edgesRef, where('targetNodeKey', '==', nodeKey))),
      ]))
    );

    snaps.forEach((snap) => {
      snap.docs.forEach((docSnap) => {
        if (!docSnap?.id) return;
        if (edgeMap.has(docSnap.id)) return;
        edgeMap.set(docSnap.id, { id: docSnap.id, ...(docSnap.data() || {}) });
      });
    });
  }

  return Array.from(edgeMap.values());
}

function canonicalizeTypePair(typeKeyA, typeKeyB) {
  const left = normalize(typeKeyA);
  const right = normalize(typeKeyB);
  if (!left || !right) return null;
  if (left <= right) {
    return {
      sourceTypeKey: left,
      targetTypeKey: right,
      pairKey: `${left}__${right}`,
    };
  }
  return {
    sourceTypeKey: right,
    targetTypeKey: left,
    pairKey: `${right}__${left}`,
  };
}

function buildTemplateNodeTypeRelationMap({
  templateNodeTypes = [],
  workspaceNodes = [],
  workspaceEdges = [],
}) {
  const nodeTypeByNodeKey = new Map();
  safeArray(workspaceNodes).forEach((nodeRow) => {
    const nodeKey = normalize(nodeRow?.key);
    const typeKey = normalize(nodeRow?.type);
    if (!nodeKey || !typeKey) return;
    nodeTypeByNodeKey.set(nodeKey, typeKey);
  });

  const templateTypeKeys = new Set(
    safeArray(templateNodeTypes)
      .map((row) => normalize(row?.key))
      .filter(Boolean)
  );

  const pairMap = new Map();

  safeArray(workspaceEdges).forEach((edgeRow) => {
    const sourceNodeKey = normalize(edgeRow?.sourceNodeKey);
    const targetNodeKey = normalize(edgeRow?.targetNodeKey);
    if (!sourceNodeKey || !targetNodeKey) return;

    const sourceTypeKey = nodeTypeByNodeKey.get(sourceNodeKey) || null;
    const targetTypeKey = nodeTypeByNodeKey.get(targetNodeKey) || null;
    if (!sourceTypeKey || !targetTypeKey) return;

    if (templateTypeKeys.size > 0) {
      if (!templateTypeKeys.has(sourceTypeKey) || !templateTypeKeys.has(targetTypeKey)) {
        return;
      }
    }

    const canonical = canonicalizeTypePair(sourceTypeKey, targetTypeKey);
    if (!canonical) return;
    if (pairMap.has(canonical.pairKey)) return;

    pairMap.set(canonical.pairKey, {
      sourceTypeKey: canonical.sourceTypeKey,
      targetTypeKey: canonical.targetTypeKey,
    });
  });

  return Array.from(pairMap.values()).sort((a, b) => {
    const left = `${a.sourceTypeKey}__${a.targetTypeKey}`;
    const right = `${b.sourceTypeKey}__${b.targetTypeKey}`;
    return left.localeCompare(right);
  });
}

function buildTemplateNodeTypeSeedMap(templateNodeTypes = []) {
  const rows = [];

  safeArray(templateNodeTypes).forEach((typeRow) => {
    const typeKey = normalize(typeRow?.key);
    if (!typeKey) return;

    const label = normalize(typeRow?.label) || 'item';
    rows.push({
      typeKey,
      seedTitle: `New ${label}`.trim(),
    });
  });

  rows.sort((a, b) => a.typeKey.localeCompare(b.typeKey));
  return rows;
}

function buildTemplateDatabases(workspaceDatabases = []) {
  return safeArray(workspaceDatabases).map((row) => {
    const cleaned = cleanDoc(row);
    const key = normalize(cleaned?.key);
    if (!key) return null;

    return {
      ...cleaned,
      key,
      sourceId: normalize(row?.id) || null,
    };
  }).filter(Boolean);
}

function buildTemplateViews(workspaceViews = [], databaseTypeByKey = new Map()) {
  const views = safeArray(workspaceViews).filter((view) => {
    const nodeKey = normalize(view?.nodeKey);
    const databaseKey = normalize(view?.databaseKey);
    const nodeType = normalize(view?.nodeType || view?.typeKey)
      || normalize(databaseTypeByKey.get(databaseKey));
    const type = normalize(view?.type);

    if (!nodeType || !type) return false;
    if (nodeKey) return false;
    return Boolean(databaseKey || nodeType);
  });

  const databaseViews = [];
  const nodeTypeViews = [];

  views.forEach((view) => {
    const cleaned = cleanDoc(view);
    const databaseKey = normalize(cleaned?.databaseKey);
    const resolvedNodeType = normalize(cleaned?.nodeType || cleaned?.typeKey)
      || normalize(databaseTypeByKey.get(databaseKey));
    const key = normalize(cleaned?.key);
    if (!key) return;
    const normalized = {
      ...cleaned,
      key,
      sourceId: normalize(view?.id) || null,
      nodeKey: null,
      nodeType: resolvedNodeType || null,
      databaseKey: databaseKey || null,
      scope: databaseKey ? 'DATABASE' : 'NODE_TYPE',
    };

    if (databaseKey) {
      databaseViews.push(normalized);
      return;
    }

    nodeTypeViews.push(normalized);
  });

  return { databaseViews, nodeTypeViews };
}

function buildTemplateGlobalDashboards(workspaceDashboards = []) {
  return safeArray(workspaceDashboards)
    .filter((dashboard) => {
      const scope = normalize(dashboard?.scope || 'GLOBAL').toUpperCase();
      return scope === 'GLOBAL';
    })
    .map((dashboard) => {
      const cleaned = cleanDoc(dashboard);
      const key = normalize(cleaned?.key);
      if (!key) return null;
      return {
        ...cleaned,
        key,
        scope: 'GLOBAL',
        anchorTypeKey: null,
        cards: safeArray(cleaned?.cards).map((card) => ({
          ...card,
          key: normalize(card?.key) || generateKey(),
          viewId: normalize(card?.viewId) || null,
        })),
      };
    })
    .filter(Boolean);
}

function buildTemplateNodeTypeDashboards(workspaceDashboards = []) {
  return safeArray(workspaceDashboards)
    .filter((dashboard) => {
      const scope = normalize(dashboard?.scope || 'GLOBAL').toUpperCase();
      if (scope !== 'NODE_TYPE') return false;
      return Boolean(normalize(dashboard?.anchorTypeKey));
    })
    .map((dashboard) => {
      const cleaned = cleanDoc(dashboard);
      const anchorTypeKey = normalize(cleaned?.anchorTypeKey);
      const key = normalize(cleaned?.key);
      if (!key) return null;
      return {
        ...cleaned,
        key,
        scope: 'NODE_TYPE',
        anchorTypeKey,
        cards: safeArray(cleaned?.cards).map((card) => ({
          ...card,
          key: normalize(card?.key) || generateKey(),
          viewId: normalize(card?.viewId) || null,
        })),
      };
    })
    .filter(Boolean);
}

export async function isTemplateSuperAdmin() {
  return isCurrentUserSuperUser();
}

async function requireTemplateSuperAdmin(message = 'Only super-admin can manage templates.') {
  if (!auth.currentUser) {
    throw new Error('You must be signed in to manage templates.');
  }

  const allowed = await isTemplateSuperAdmin();
  if (!allowed) {
    throw new Error(message);
  }
}

export async function publishTemplateSnapshot({
  workspaceKey,
  title,
  description,
  authorKey,
  authorEmail,
}) {
  const cleanedWorkspaceKey = normalize(workspaceKey);
  const cleanedTitle = normalize(title);
  const cleanedAuthorEmail = normalizeLower(authorEmail);

  if (!cleanedWorkspaceKey) throw new Error('publishTemplateSnapshot: workspaceKey is required');
  if (!cleanedTitle) throw new Error('publishTemplateSnapshot: title is required');
  await requireTemplateSuperAdmin('Only super-admin can publish templates.');

  const [nodeTypes, collectionsConfig, workspaceViews, workspaceDashboards] = await Promise.all([
    fetchWorkspaceNodeTypes(cleanedWorkspaceKey),
    fetchWorkspaceDatabases(cleanedWorkspaceKey),
    fetchWorkspaceViews(cleanedWorkspaceKey),
    fetchWorkspaceDashboards(cleanedWorkspaceKey),
  ]);

  const workspaceNodes = await fetchWorkspaceNodes(cleanedWorkspaceKey);
  const workspaceEdges = await fetchWorkspaceIncidentEdges(workspaceNodes);

  const templateDatabases = buildTemplateDatabases(collectionsConfig);
  const databaseTypeByKey = new Map();
  templateDatabases.forEach((databaseRow) => {
    const typeKey = normalize(databaseRow?.type);
    if (!typeKey) return;
    collectAliases(databaseRow?.key, databaseRow?.sourceId).forEach((alias) => {
      databaseTypeByKey.set(alias, typeKey);
    });
  });

  const { databaseViews, nodeTypeViews } = buildTemplateViews(workspaceViews, databaseTypeByKey);
  const globalDashboards = buildTemplateGlobalDashboards(workspaceDashboards);
  const nodeTypeDashboards = buildTemplateNodeTypeDashboards(workspaceDashboards);
  const nodeTypeRelationMap = buildTemplateNodeTypeRelationMap({
    templateNodeTypes: nodeTypes,
    workspaceNodes,
    workspaceEdges,
  });
  const nodeTypeSeedMap = buildTemplateNodeTypeSeedMap(nodeTypes);

  const now = Timestamp.now();
  const templateKey = generateKey();

  const templateDoc = {
    key: templateKey,
    title: cleanedTitle,
    titleLower: normalizeLower(cleanedTitle),
    description: normalize(description) || '',
    authorKey: normalize(authorKey) || null,
    authorEmail: cleanedAuthorEmail || null,
    status: 'active',
    revision: 1,
    workspaceSourceKey: cleanedWorkspaceKey,
    createdAt: now,
    updatedAt: now,
    payload: {
      nodeTypes: nodeTypes.map(cleanDoc),
      databases: templateDatabases.map(cleanDoc),
      databaseViews: databaseViews.map(cleanDoc),
      nodeTypeViews: nodeTypeViews.map(cleanDoc),
      globalDashboards: globalDashboards.map(cleanDoc),
      nodeTypeDashboards: nodeTypeDashboards.map(cleanDoc),
      nodeTypeRelationMap: nodeTypeRelationMap.map(cleanDoc),
      nodeTypeSeedMap: nodeTypeSeedMap.map(cleanDoc),
      moduleNav: {
        sections: [],
      },
    },
  };

  await setDoc(doc(db, collections.templates, templateKey), templateDoc, { merge: false });
  return templateDoc;
}

export async function listActiveTemplates() {
  const snap = await getDocs(collection(db, collections.templates));
  const rows = snap.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) }));

  return rows
    .filter((row) => normalizeLower(row?.status || 'active') !== 'archived')
    .sort((a, b) => toMillis(b?.updatedAt) - toMillis(a?.updatedAt));
}

export async function listTemplatesAdmin({ includeArchived = true } = {}) {
  const snap = await getDocs(collection(db, collections.templates));
  const rows = snap.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) }));

  const filtered = includeArchived
    ? rows
    : rows.filter((row) => normalizeLower(row?.status || 'active') !== 'archived');

  return filtered.sort((a, b) => toMillis(b?.updatedAt) - toMillis(a?.updatedAt));
}

export async function uploadTemplateScreenshot({ file, templateKey }) {
  const imageFile = file;
  if (!(imageFile instanceof File)) {
    throw new Error('Select an image file to upload.');
  }

  const mimeType = String(imageFile?.type || '').toLowerCase();
  if (!mimeType.startsWith('image/')) {
    throw new Error('Only image files are allowed for template screenshots.');
  }

  const cleanedTemplateKey = normalize(templateKey) || 'draft';
  const extension = normalize(imageFile?.name).split('.').pop() || 'jpg';
  const path = `templates/screenshots/${cleanedTemplateKey}-${Date.now()}.${extension}`;
  const storageRef = ref(storage, path);

  await uploadBytes(storageRef, imageFile, {
    cacheControl: 'public,max-age=86400',
    contentType: mimeType,
  });

  return getDownloadURL(storageRef);
}

function normalizeTemplatePayload(rawPayload) {
  const payload = rawPayload && typeof rawPayload === 'object' ? rawPayload : {};

  return {
    nodeTypes: safeArray(payload.nodeTypes).map(cleanDoc),
    databases: safeArray(payload.databases).map(cleanDoc),
    databaseViews: safeArray(payload.databaseViews).map(cleanDoc),
    nodeTypeViews: safeArray(payload.nodeTypeViews).map(cleanDoc),
    globalDashboards: safeArray(payload.globalDashboards).map(cleanDoc),
    nodeTypeDashboards: safeArray(payload.nodeTypeDashboards).map(cleanDoc),
    nodeTypeRelationMap: safeArray(payload.nodeTypeRelationMap).map(cleanDoc),
    nodeTypeSeedMap: safeArray(payload.nodeTypeSeedMap).map(cleanDoc),
    moduleNav: payload?.moduleNav && typeof payload.moduleNav === 'object'
      ? cleanDoc(payload.moduleNav)
      : { sections: [] },
  };
}

function pickDefined(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
}

function resolveTemplateDocFromDefinition({
  definition,
  title,
  description,
  screenshotUrl,
  authorKey,
  authorEmail,
  status,
  revision,
  workspaceSourceKey,
  templateKey,
}) {
  const source = definition && typeof definition === 'object' ? definition : {};
  const sourcePayload = source?.payload && typeof source.payload === 'object'
    ? source.payload
    : source;

  const payload = normalizeTemplatePayload(sourcePayload);

  const now = Timestamp.now();
  const key = normalize(templateKey) || normalize(source?.key) || generateKey();
  const resolvedTitle = normalize(title) || normalize(source?.title);
  const resolvedAuthorEmail = normalizeLower(authorEmail || source?.authorEmail);

  if (!resolvedTitle) {
    throw new Error('Template title is required.');
  }

  return {
    key,
    title: resolvedTitle,
    titleLower: normalizeLower(resolvedTitle),
    description: normalize(pickDefined(description, source?.description)) || '',
    screenshotUrl: normalize(pickDefined(screenshotUrl, source?.screenshotUrl)) || '',
    authorKey: normalize(pickDefined(authorKey, source?.authorKey)) || null,
    authorEmail: resolvedAuthorEmail || null,
    status: normalizeLower(pickDefined(status, source?.status, 'active')) || 'active',
    revision: Number.isFinite(Number(pickDefined(revision, source?.revision)))
      && Number(pickDefined(revision, source?.revision)) > 0
      ? Number(pickDefined(revision, source?.revision))
      : 1,
    workspaceSourceKey: normalize(pickDefined(workspaceSourceKey, source?.workspaceSourceKey)) || null,
    createdAt: source?.createdAt || now,
    updatedAt: now,
    payload,
  };
}

export async function createTemplateFromDefinition({
  definition,
  title,
  description,
  screenshotUrl,
  authorKey,
  authorEmail,
  status,
  revision,
  workspaceSourceKey,
  templateKey,
}) {
  await requireTemplateSuperAdmin('Only super-admin can create templates.');

  const templateDoc = resolveTemplateDocFromDefinition({
    definition,
    title,
    description,
    screenshotUrl,
    authorKey,
    authorEmail,
    status,
    revision,
    workspaceSourceKey,
    templateKey,
  });

  await setDoc(doc(db, collections.templates, templateDoc.key), templateDoc, { merge: false });
  return templateDoc;
}

export async function updateTemplateFromDefinition({
  templateKey,
  definition,
  title,
  description,
  screenshotUrl,
  authorKey,
  authorEmail,
  status,
  revision,
  workspaceSourceKey,
}) {
  await requireTemplateSuperAdmin('Only super-admin can update templates.');

  const cleanedTemplateKey = normalize(templateKey);
  if (!cleanedTemplateKey) {
    throw new Error('Template key is required for update.');
  }

  const templateDoc = resolveTemplateDocFromDefinition({
    definition,
    title,
    description,
    screenshotUrl,
    authorKey,
    authorEmail,
    status,
    revision,
    workspaceSourceKey,
    templateKey: cleanedTemplateKey,
  });

  await setDoc(doc(db, collections.templates, cleanedTemplateKey), templateDoc, { merge: false });
  return templateDoc;
}

export async function deleteTemplateByKey({ templateKey, authorEmail }) {
  void authorEmail;
  await requireTemplateSuperAdmin('Only super-admin can delete templates.');

  const cleanedTemplateKey = normalize(templateKey);
  if (!cleanedTemplateKey) {
    throw new Error('Template key is required for delete.');
  }

  await deleteDoc(doc(db, collections.templates, cleanedTemplateKey));
}

export function subscribeToInstalledModules(workspaceKey, callback) {
  if (typeof callback !== 'function') return () => { };
  const cleanedWorkspaceKey = normalize(workspaceKey);
  if (!cleanedWorkspaceKey) {
    callback([]);
    return () => { };
  }

  const q = query(collection(db, collections.modules), where('workspaceKey', '==', cleanedWorkspaceKey));

  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) }));
      rows.sort((a, b) => toMillis(b?.appliedAt) - toMillis(a?.appliedAt));
      callback(rows);
    },
    (error) => {
      console.error('[TemplateRuntimeService] subscribeToInstalledModules error:', error);
      callback([]);
    }
  );
}

export function findInstalledModuleForTemplate(modules, templateKey) {
  const cleanedTemplateKey = normalize(templateKey);
  if (!cleanedTemplateKey) return null;

  const rows = safeArray(modules);
  for (const row of rows) {
    const rowTemplateKey = normalize(row?.templateKey);
    const status = normalizeLower(row?.status || 'installed');
    if (rowTemplateKey !== cleanedTemplateKey) continue;
    if (status && status !== 'installed') continue;
    return row || null;
  }

  return null;
}

export function isTemplateInstalledInWorkspace(modules, templateKey) {
  return Boolean(findInstalledModuleForTemplate(modules, templateKey));
}

export async function applyTemplateToWorkspace({ workspaceKey, templateKey, appliedBy }) {
  const cleanedWorkspaceKey = normalize(workspaceKey);
  const cleanedTemplateKey = normalize(templateKey);

  if (!cleanedWorkspaceKey) throw new Error('applyTemplateToWorkspace: workspaceKey is required');
  if (!cleanedTemplateKey) throw new Error('applyTemplateToWorkspace: templateKey is required');

  const fn = httpsCallable(functions, 'applyTemplateToWorkspace');
  const { data } = await fn({
    workspaceKey: cleanedWorkspaceKey,
    templateKey: cleanedTemplateKey,
    appliedBy: normalize(appliedBy) || null,
  });

  return data || null;
}

export async function removeModuleForce({ workspaceKey, moduleKey }) {
  const cleanedWorkspaceKey = normalize(workspaceKey);
  const cleanedModuleKey = normalize(moduleKey);

  if (!cleanedWorkspaceKey) throw new Error('removeModuleForce: workspaceKey is required');
  if (!cleanedModuleKey) throw new Error('removeModuleForce: moduleKey is required');

  const fn = httpsCallable(functions, 'removeModuleFromWorkspace');
  const { data } = await fn({
    workspaceKey: cleanedWorkspaceKey,
    moduleKey: cleanedModuleKey,
  });

  return data || null;
}